import { Immutable } from "immer";
import React from "react";

import { templates } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import {
    configuratorPromoLineComponentsMap,
    footerComponentsMap,
} from "../blocks/componentMaps";
import { PageSkeleton } from "../components/PageSkeleton";
import { ProductLinePageBody as ProductLinePageBodyContent } from "../components/ProductPageBody";
import { Template as ModularConfigurator } from "./ModularConfiguratorTemplate";
import { Template as ProductHeroAssembly } from "./ProductHeroAssemblyTemplate";

const ProductLinePageFinePrint = (
    props: Immutable<{ page: templates.ProductLinePageTemplate["page"] }>,
): React.JSX.Element | null => {
    if (!props.page.fine_print) {
        return null;
    }
    return (
        <>
            {props.page.fine_print.value.map((block) => (
                <StreamField
                    key={block.id}
                    tagName="div"
                    attrs={{
                        className: `w-block-${block.type} block-${block.type}`,
                    }}
                    value={[block]}
                    components={footerComponentsMap}
                />
            ))}
        </>
    );
};

const ProductLinePageConfiguratorPromoLine = (
    props: Immutable<{ page: templates.ProductLinePageTemplate["page"] }>,
): React.JSX.Element => (
    <>
        {props.page.configurator_promo_line.value.map((block) => (
            <StreamField
                key={block.id}
                tagName="div"
                attrs={{
                    className: `w-block-${block.type} block-${block.type}`,
                }}
                value={[block]}
                components={configuratorPromoLineComponentsMap}
            />
        ))}
    </>
);

const ProductLinePageBody = (
    props: Immutable<{
        page: templates.ProductLinePageTemplate["page"];
        reviews: templates.ProductLinePageTemplate["reviews"];
        change_root_ssr: templates.ProductLinePageTemplate["change_root_ssr"];
        root_conditional_ssr: templates.ProductLinePageTemplate["root_conditional_ssr"];
    }>,
): React.JSX.Element | null => {
    if (!props.page.body) {
        return null;
    }
    return (
        <ProductLinePageBodyContent
            value={props.page.body.value.filter(
                (block) => block.type !== "gallery_block",
            )}
            reviews={props.reviews}
            changeRootSSR={props.change_root_ssr}
            rootConditionalSSR={props.root_conditional_ssr}
            pageSlug={props.page.slug}
        />
    );
};

export const Template = (
    props: Immutable<templates.ProductLinePageTemplate>,
): React.JSX.Element => {
    const { page } = props;
    return (
        <PageSkeleton
            pageChrome={props.page_chrome}
            afterFooter={<ProductLinePageFinePrint page={page} />}
        >
            <section className="product-hero">
                <div className="product-hero__blocks">
                    <ProductHeroAssembly {...props.hero} />
                </div>
                <div
                    className={`product-hero__configurator ${page.configurator_background_style || ""}`}
                >
                    <div className="product-hero__configurator__promo-line">
                        <ProductLinePageConfiguratorPromoLine page={page} />
                    </div>
                    <ModularConfigurator {...props.configurator} />
                </div>
            </section>

            <section className={`pdp-blocks pdp-blocks--${page.slug}`}>
                <ProductLinePageBody
                    page={page}
                    reviews={props.reviews}
                    change_root_ssr={props.change_root_ssr}
                    root_conditional_ssr={props.root_conditional_ssr}
                />
            </section>
        </PageSkeleton>
    );
};
